import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider } from 'antd';
import { FormattedMessage } from 'react-intl';
import Pficon from 'components/Pficon';
import { countBy } from 'lodash';

import messages from 'containers/TemplatePreviewPage/TemplateFlows/ComplexFlow/messages';
import {
  getDefaultVariableValue,
  hasFieldError,
} from 'containers/TemplatePreviewPage/utils';
import classNames from 'classnames';
import { getConjointNumericValue } from 'containers/QuestionnairePage/QuestionGroup/utils';
import { CONJOINT_ATTRIBUTE_TYPES } from 'containers/QuestionnairePage/QuestionGroup/Conjoint/constants';

import { FIELD_TYPE } from 'containers/TemplatePreviewPage/constants';
import TextWithMedia from '../TextWithMedia';
import TextWithMediaRepeater from '../TextWithMediaRepeater';
import BooleanType from '../BooleanType';
import NumericAndTextRepeater from '../NumericAndTextRepeater';

const AttributeRepeater = ({
  question,
  questionIndex,
  onUpdateVariable,
  skipTopMargin,
}) => {
  const {
    value: attributes = [],
    hasError,
    isPristine,
    body,
    placeholder,
    min: minAttributes = 0,
    max: maxAttributes = 100,
  } = question;

  const underMinCount = Math.max(minAttributes - attributes.length, 0);
  const overMaxCount = Math.max(attributes.length - maxAttributes, 0);
  const attributesByType = countBy(attributes, 'type');
  const canAddAttribute = maxAttributes > 1;
  const hasMoreThanOnePriceAttributes =
    attributesByType[CONJOINT_ATTRIBUTE_TYPES.PRICE] > 1;

  const missingMessage =
    underMinCount === 1
      ? messages.validation.fieldIncomplete
      : messages.validation.fieldIncompletePlural;

  const extraValuesMessage =
    overMaxCount === 1
      ? messages.validation.fieldHasExtra
      : messages.validation.fieldHasExtraPlural;

  const onUpdate = useCallback(
    (partialValue, updateIndex) => {
      const updatedValues = attributes.map((existingValue, index) => {
        if (index === updateIndex) {
          return { ...existingValue, ...partialValue };
        }
        return existingValue;
      });
      onUpdateVariable(updatedValues);
    },
    [onUpdateVariable, attributes],
  );

  const onUpdateSingleTextWithMediaValue = useCallback(
    (index) => (value) => {
      onUpdate({ name: value.text, media: value.media }, index);
    },
    [onUpdate],
  );

  const onUpdateAttributeType = useCallback(
    (index) => (value) => {
      const attribute = attributes[index];
      const attributeLevels = attribute.values;
      const newAttributeLevels = attributeLevels.map((attributeLevel) => ({
        text: attributeLevel.text,
        media: attributeLevel.media,
        numericValue: getConjointNumericValue(attributeLevel.text, value),
      }));
      onUpdate({ type: value, values: newAttributeLevels }, index);
    },
    [onUpdate, attributes],
  );

  const onAddAttribute = useCallback(() => {
    onUpdateVariable([
      ...attributes,
      getDefaultVariableValue({ fieldType: FIELD_TYPE.ATTRIBUTE }),
    ]);
  }, [onUpdateVariable, attributes]);

  const onRemoveAttribute = useCallback(
    (indexToRemove) => {
      onUpdateVariable(
        attributes.filter((_, index) => index !== indexToRemove),
      );
    },
    [onUpdateVariable, attributes],
  );

  const onUpdateAttributeLevel = useCallback(
    (index) => (attributeLevels) => {
      onUpdate({ values: attributeLevels }, index);
    },
    [onUpdate],
  );

  const addAttributeDisabled = attributes.length >= maxAttributes;

  return (
    <ul className="flex flex-col margin-none padding-none">
      {attributes.map((attribute = {}, index) => {
        const subTextQuestion = {
          body,
          fieldType: FIELD_TYPE.TEXT_WITH_MEDIA,
          value: { text: attribute.name, media: attribute.media },
          placeholder,
          required: true,
          isPristine,
          supportedMediaTypes: ['image'],
        };
        const attributeTypeQuestion = {
          name: 'type',
          value: attribute.type,
          body: null,
          options: [
            {
              body: 'Feature type',
              value: CONJOINT_ATTRIBUTE_TYPES.FEATURE,
              order: 1,
              selected: true,
            },
            {
              body: 'Price type',
              info: 'You can add up to 1 Price type attribute, to get the Marginal Willingness to Pay chart at the analysis. When using a Price attribute, we suggest that: none of the levels of price is zero and if there are more than two levels, they should have approximately equal gaps between them.',
              value: CONJOINT_ATTRIBUTE_TYPES.PRICE,
              order: 2,
            },
          ],
        };
        const attributeLevelsQuestion = {
          name: 'values',
          body: 'Levels',
          value: attribute.values,
          placeholder: 'Type a level',
          min: 2,
          max: Infinity,
          supportedMediaTypes: ['image'],
        };
        subTextQuestion.hasError = hasFieldError(subTextQuestion);
        const deleteDisabled = attributes.length <= minAttributes;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <li className="flex" key={index}>
            <div className="flex-1">
              <TextWithMedia
                skipTopMargin={index === 0 && skipTopMargin}
                question={subTextQuestion}
                autoFocus={questionIndex === 0}
                onUpdateVariable={onUpdateSingleTextWithMediaValue(index)}
                hideErrors
              />
              <div className="margin-vertical-xs">
                <BooleanType
                  question={attributeTypeQuestion}
                  onUpdateVariable={onUpdateAttributeType(index)}
                />
              </div>
              <div className="margin-top-xs">
                {attribute.type === CONJOINT_ATTRIBUTE_TYPES.PRICE && (
                  <NumericAndTextRepeater
                    addButtonText="Add level"
                    question={attributeLevelsQuestion}
                    onUpdateVariable={onUpdateAttributeLevel(index)}
                  />
                )}
                {attribute.type !== CONJOINT_ATTRIBUTE_TYPES.PRICE && (
                  <TextWithMediaRepeater
                    question={attributeLevelsQuestion}
                    onUpdateVariable={onUpdateAttributeLevel(index)}
                    addButtonText="Add level"
                    buttonStyle={{ height: 'auto' }}
                    buttonClassNames={{
                      'text-size-secondary': true,
                      'padding-horizontal-xs': true,
                      'padding-vertical-xxs': true,
                    }}
                  />
                )}
              </div>
              {canAddAttribute && (
                <Divider
                  type="horizontal"
                  className={classNames(
                    'margin-top-lg',
                    'margin-btm-none',
                    'background-grey-5',
                  )}
                />
              )}
            </div>

            {canAddAttribute && (
              <Fragment>
                <Divider
                  type="vertical"
                  className={classNames(
                    'horizontal-margin-xs',
                    'background-grey-5',
                    'height-none',
                    {
                      'margin-top-lg margin-btm-none': !(
                        index === 0 && skipTopMargin
                      ),
                      'margin-vertical-none': index === 0 && skipTopMargin,
                    },
                  )}
                />
                <Button
                  disabled={deleteDisabled}
                  type="button"
                  onClick={() => onRemoveAttribute(index)}
                  style={{
                    height: 38,
                    lineHeight: '38px',
                    marginTop: index === 0 && skipTopMargin ? 30 : 54,
                  }}
                  className={classNames(
                    'outline-blue',
                    'margin-top-xxs',
                    'color-grey-10',
                    'text-align-center',
                    'padding-vertical-none',
                    'padding-horizontal-xs',
                    'border-none',
                    'background-transparent',
                    {
                      'color-grey-6': deleteDisabled,
                      'color-grey-6-hover': deleteDisabled,
                      'color-grey-10': !deleteDisabled,
                    },
                  )}
                >
                  <Pficon type="close" className="text-size-h3" />
                </Button>
              </Fragment>
            )}
          </li>
        );
      })}
      {!isPristine && hasError && Boolean(underMinCount) && (
        <li className="display-block margin-btm-sm">
          <span className="color-red-6" key={`${question.id}-validation`}>
            <FormattedMessage
              {...missingMessage}
              values={{
                missingValuesCount: underMinCount,
              }}
            />
          </span>
        </li>
      )}
      {!isPristine && hasError && Boolean(overMaxCount) && (
        <li className="display-block margin-btm-sm">
          <span className="color-red-6" key={`${question.id}-validation-count`}>
            <FormattedMessage
              {...extraValuesMessage}
              values={{
                extraValuesCount: overMaxCount,
              }}
            />
          </span>
        </li>
      )}
      {!isPristine && hasError && hasMoreThanOnePriceAttributes && (
        <li className="display-block margin-btm-sm">
          <span className="color-red-6" key={`${question.id}-validation-count`}>
            <FormattedMessage {...messages.validation.fieldHasMorePriceTypes} />
          </span>
        </li>
      )}
      {!isPristine &&
        hasError &&
        !(underMinCount || overMaxCount || hasMoreThanOnePriceAttributes) && (
          <li className="display-block margin-vertical-sm">
            <span className="color-red-6">
              <FormattedMessage {...messages.validation.fieldHasSubError} />
            </span>
          </li>
        )}

      {canAddAttribute && (
        <li className="display-block margin-top-lg">
          <Button
            type="button"
            onClick={onAddAttribute}
            disabled={addAttributeDisabled}
            className={classNames(
              'text-size-secondary',
              'color-grey-1',
              'border-none',
              'padding-horizontal-xs',
              'padding-vertical-xxs',
              'shadow-none',
              {
                'background-grey-8': addAttributeDisabled,
                'background-grey-8-hover': addAttributeDisabled,
                'background-grey-10': !addAttributeDisabled,
              },
            )}
            style={{ height: 'auto' }}
          >
            Add attribute
          </Button>
        </li>
      )}
    </ul>
  );
};

AttributeRepeater.propTypes = {
  question: PropTypes.object,
  questionGroupIndex: PropTypes.number,
  questionIndex: PropTypes.number,
  onUpdateVariable: PropTypes.func.isRequired,
};

export default AttributeRepeater;
